/**
 * mock.ts — deterministic DMS fixtures (Story dms/M11), the V10-J
 * mock-mode-is-the-dev/test-default precedent (usePolicyRows / useCourtage).
 *
 * Six Ablage rows (mixed state/category/source) + one matching detail with a
 * full "Verknüpfte Objekte" set (Dokument implicit + Kunde + Police + 2×Aufgabe)
 * so the viewer renders every card kind in mock mode without a backend.
 *
 * `dedup_status`/`duplicate_of_document_id` (DMS Part 2, P2-10/P2-12): every row
 * here is `'unique'` — the productive-stock default. `DUPLICATES_MOCK` below is the
 * separate admin "Duplikate" view fixture (mock mode never mixes the two lists,
 * mirroring the live `GET /documents/duplicates` endpoint's own dedicated shape).
 *
 * [Source: apps/web/src/features/entity-detail/mock.ts] (feature-mock precedent)
 * [Source: projects/broker-ag/dms/spec.md §4.1, §4.5]
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §2.4, P2-12]
 */
import type { DocumentDetailDto, DocumentListItemDto, DuplicateInfoDto } from '@brokly/shared';

export const DOCUMENTS_MOCK: DocumentListItemDto[] = [
  {
    id: 'doc-swi-0002603',
    title: 'Police-SWI-2024-0002603.pdf',
    category: 'Police',
    state: 'active',
    bezug: 'Helvetia Treuhand GmbH · Lebensversicherung',
    quelle: 'Posteingang · Swica',
    doc_date: '2026-07-27T09:12:00.000Z',
    size_bytes: 297_886,
    version_no: 2,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
  {
    id: 'doc-swi-0002603-v1',
    title: 'Police-SWI-2024-0002603.pdf',
    category: 'Police',
    state: 'inactive',
    bezug: 'Helvetia Treuhand GmbH · Lebensversicherung',
    quelle: 'Posteingang · Swica',
    doc_date: '2025-11-03T09:12:00.000Z',
    size_bytes: 291_004,
    version_no: 1,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
  {
    id: 'doc-avb-swica-leben',
    title: 'AVB-Swica-Lebensversicherung.pdf',
    category: 'AVB',
    state: 'active',
    bezug: 'Helvetia Treuhand GmbH · Lebensversicherung',
    quelle: 'Posteingang · Swica',
    doc_date: '2026-07-27T09:12:00.000Z',
    size_bytes: 416_768,
    version_no: 1,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
  {
    id: 'doc-zur-0006028',
    title: 'Police-ZUR-2024-0006028.pdf',
    category: 'Police',
    state: 'active',
    bezug: 'Basler Pharma Solutions AG · Lebensversicherung',
    quelle: 'Posteingang · Zurich',
    doc_date: '2026-06-14T08:00:00.000Z',
    size_bytes: 278_528,
    version_no: 1,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
  {
    id: 'doc-manual-mandat',
    title: 'Maklermandat_Romandie_Conseil.pdf',
    category: 'Mandat',
    state: 'active',
    bezug: 'Romandie Conseil Financier SA',
    quelle: 'Manuell',
    doc_date: '2026-05-02T14:30:00.000Z',
    size_bytes: 88_310,
    version_no: 1,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
  {
    id: 'doc-scan-schaden',
    title: 'Schadenmeldung_2026-04.pdf',
    category: 'Schaden',
    state: 'inactive',
    bezug: 'Schaffhauser Präzision AG',
    quelle: 'Scan-Center · Dataxx',
    doc_date: '2026-04-11T11:05:00.000Z',
    size_bytes: 152_004,
    version_no: 1,
    dedup_status: 'unique',
    duplicate_of_document_id: null,
  },
];

/**
 * DUPLICATE_DOCUMENTS_MOCK — the two flagged-duplicate document rows for the admin
 * "Duplikate" view (P2-12). Deliberately kept OUT of `DOCUMENTS_MOCK` (the productive
 * Ablage grid's fixture) — mirrors the live default filter (`dedup_status='unique'`):
 * mock mode's default `useDocumentRows` never needs to filter anything out because
 * these rows simply aren't in that array, same end state as the live query.
 */
export const DUPLICATE_DOCUMENTS_MOCK: DocumentListItemDto[] = [
  {
    id: 'doc-swi-0002603-dup',
    title: 'Police-SWI-2024-0002603 (2).pdf',
    category: 'Police',
    state: 'active',
    bezug: 'Helvetia Treuhand GmbH · Lebensversicherung',
    quelle: 'Posteingang · Swica',
    doc_date: '2026-08-18T10:04:00.000Z',
    size_bytes: 297_886,
    version_no: 1,
    dedup_status: 'duplicate',
    duplicate_of_document_id: 'doc-swi-0002603',
  },
  {
    id: 'doc-zur-0006028-dup',
    title: 'Police-ZUR-2024-0006028_scan.pdf',
    category: 'Police',
    state: 'active',
    bezug: 'Basler Pharma Solutions AG · Lebensversicherung',
    quelle: 'Scan-Center · Dataxx',
    doc_date: '2026-08-15T07:41:00.000Z',
    size_bytes: 279_014,
    version_no: 1,
    dedup_status: 'duplicate',
    duplicate_of_document_id: 'doc-zur-0006028',
  },
];

/**
 * DUPLICATES_MOCK — the admin "Duplikate" view's method/score/detected_at fixture
 * (P2-12). Mirrors `GET /documents/duplicates` (`DuplicateInfoDto[]`) — one row per
 * `DUPLICATE_DOCUMENTS_MOCK` entry, keyed by `document_id`: an exact `file_hash` hit
 * and a structured-identity `content_key` hit. `score: null` for both — exact methods
 * never carry a similarity score (only Tier 3/`embedding` would).
 */
export const DUPLICATES_MOCK: DuplicateInfoDto[] = [
  {
    document_id: 'doc-swi-0002603-dup',
    matched_document_id: 'doc-swi-0002603',
    method: 'file_hash',
    score: null,
    detected_at: '2026-08-18T10:04:00.000Z',
  },
  {
    document_id: 'doc-zur-0006028-dup',
    matched_document_id: 'doc-zur-0006028',
    method: 'content_key',
    score: null,
    detected_at: '2026-08-15T07:41:00.000Z',
  },
];

/** Detail for `doc-swi-0002603` — the mock viewer target (screenshots 08/10). */
export const DOCUMENT_DETAIL_MOCK: DocumentDetailDto = {
  id: 'doc-swi-0002603',
  linked_type: 'communication',
  linked_id: 'comm-helvetia-001',
  kind: 'police_neu',
  type: 'policy',
  filename: 'Police-SWI-2024-0002603.pdf',
  title: 'Police-SWI-2024-0002603.pdf',
  mime_type: 'application/pdf',
  size_bytes: 297_886,
  state: 'active',
  source: 'email',
  source_label: 'Swica',
  version_group_id: 'pol-helvetia-leben',
  version_no: 2,
  created_at: '2026-07-27T09:12:00.000Z',
  updated_at: '2026-07-27T09:12:00.000Z',
  dedup_status: 'unique',
  duplicate_of_document_id: null,
  connected_objects: [
    {
      object_type: 'firma',
      object_id: 'k-helvetia-treuhand',
      title: 'Helvetia Treuhand GmbH',
      subtitle: 'C0002',
      route: '/customers/k-helvetia-treuhand',
    },
    {
      object_type: 'policy',
      object_id: 'pol-helvetia-leben',
      title: 'Lebensversicherung',
      subtitle: 'Swica',
      route: '/policen/pol-helvetia-leben',
    },
    {
      object_type: 'task',
      object_id: 'task-qr-maerz-2026',
      title: 'E-Mail-Eingang: QR-Rechnung März 2026 (Helvetia Treuhand GmbH)',
      subtitle: 'Kundenanfrage',
      route: '/aufgaben?task=task-qr-maerz-2026',
    },
    {
      object_type: 'task',
      object_id: 'task-gwg-nachfordern',
      title: 'GwG-Dokumente nachfordern',
      subtitle: 'Compliance',
      route: '/aufgaben?task=task-gwg-nachfordern',
    },
  ],
  content_url: '/documents/doc-swi-0002603/content',
};
